import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { ansichtSichtbar } from '@asas/domain';
import type { Permission, Principal, TenantRole } from '@asas/domain';
import { DOMAIN_VERSION } from '@asas/domain/version';

import { AdminPanel, meldungFuerKontoaktion } from './AdminPanel';
import { ArbeitspaketBibliothek } from './ArbeitspaketBibliothek';
import type { CurrentSession } from './api';
import { loadSession, logout } from './api';
import { entitaetsSchluessel, geteilterSerialisierer } from './daten/serialisierung';
import { klassifiziereFehler } from './daten/fehler';
import { SessionPanel } from './SessionPanel';
import { trpc } from './trpc';
import { TrpcPanel } from './TrpcPanel';

export function App(): JSX.Element {
  const [session, setSession] = useState<CurrentSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [echo, setEcho] = useState<string | null>(null);
  const [trpcLoading, setTrpcLoading] = useState(true);
  const [trpcError, setTrpcError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('anmeldung')
      ? 'Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.'
      : null,
  );

  // AC-107: Nutzerverwaltung. `roles` kommt aus `tenant.settings`
  // (ANY_AUTHENTICATED) statt aus einer eigenen Prozedur — die Liste existiert
  // dort bereits (CLAUDE.md, Entwurfsabschnitt 3).
  const [principals, setPrincipals] = useState<readonly Principal[]>([]);
  const [roles, setRoles] = useState<readonly TenantRole[]>([]);
  // Geladen wird erst nach einem Klick — siehe `AdminPanelProps.geoeffnet`.
  const [adminGeoeffnet, setAdminGeoeffnet] = useState(false);
  const [principalsLoading, setPrincipalsLoading] = useState(false);
  const [principalsError, setPrincipalsError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<ReadonlyMap<string, string>>(new Map());
  // AC-000: die eigenen Rechte. `null`, solange sie nicht feststehen — dann
  // wird keine Ansicht mit Rechtebedarf gezeigt (siehe `ANSICHTEN`).
  const [rechte, setRechte] = useState<ReadonlySet<Permission> | null>(null);

  useEffect(() => {
    let mounted = true;

    void loadSession()
      .then((current) => {
        if (mounted) setSession(current);
      })
      .catch(() => {
        if (mounted)
          setError('Die Sitzung konnte nicht geprüft werden. Bitte versuchen Sie es erneut.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // AC-000: Ansichten nur zeigen, deren Ladeaufrufe die eigenen Rechte
  // erlauben. `session.permissions` ist ANY_AUTHENTICATED und erzeugt deshalb
  // nie ein 403 samt Audit-Zeile. Scheitert der Aufruf, bleibt alles mit
  // Rechtebedarf ausgeblendet; der Server lehnt ohnehin ab.
  useEffect(() => {
    let mounted = true;

    void trpc.session.permissions
      .query()
      .then((ergebnis) => {
        if (mounted) setRechte(new Set(ergebnis.permissions));
      })
      .catch(() => {
        if (mounted) setRechte(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Erster Aufruf ueber tRPC (AC-010). Der Endpunkt liegt hinter dem globalen
  // TenantGuard: ohne Sitzung antwortet Nest mit 401, und der Client scheitert
  // beim Auswerten. Die typisierte Behandlung dieses Falls gehoert zu AC-007.
  useEffect(() => {
    let mounted = true;

    void trpc.system.ping
      .query({ nachricht: 'hallo' })
      .then((result) => {
        if (mounted) setEcho(result.echo);
      })
      .catch(() => {
        if (mounted) setTrpcError('Der tRPC-Aufruf ist fehlgeschlagen.');
      })
      .finally(() => {
        if (mounted) setTrpcLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // AC-107: Konten und Rollen laden. Beides zusammen, weil die Oberflaeche
  // beide fuer eine einzige Ansicht braucht; scheitert `principals.list` an
  // einem fehlenden Recht, ist das die Meldung, die zaehlt — `tenant.settings`
  // ist ANY_AUTHENTICATED und scheitert praktisch nie unabhaengig davon.
  useEffect(() => {
    if (!adminGeoeffnet) {
      return;
    }

    let mounted = true;
    setPrincipalsLoading(true);
    setPrincipalsError(null);

    void Promise.all([trpc.principals.list.query(), trpc.tenant.settings.query()])
      .then(([geladenePrincipals, einstellungen]) => {
        if (mounted) {
          setPrincipals(geladenePrincipals);
          setRoles(einstellungen.roles);
        }
      })
      .catch((fehler: unknown) => {
        if (mounted) {
          setPrincipalsError(klassifiziereFehler(fehler, { schreibzugriff: false }).meldung);
        }
      })
      .finally(() => {
        if (mounted) setPrincipalsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [adminGeoeffnet]);

  /**
   * Fuehrt eine Kontoaktion aus: serialisiert je Konto (CLAUDE.md §20.2,
   * Punkt 4 — zwei Aktionen auf demselben Konto sollen einander nicht
   * ueberholen), uebernimmt nach Erfolg die SERVERANTWORT (AC-016, AK-2) statt
   * optimistisch vorzugreifen, und traegt einen Fehlschlag in die Zeile des
   * betroffenen Kontos ein statt in eine globale Meldung — die anderen Zeilen
   * bleiben bedienbar.
   */
  async function fuehreKontoaktionAus(
    principalId: string,
    aufruf: () => Promise<Principal>,
  ): Promise<void> {
    setBusyIds((vorher) => new Set(vorher).add(principalId));

    try {
      const aktualisiert = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('principal', principalId),
        aufruf,
      );

      setPrincipals((vorher) =>
        vorher.map((principal) => (principal.id === principalId ? aktualisiert : principal)),
      );
      setRowErrors((vorher) => {
        const neu = new Map(vorher);
        neu.delete(principalId);
        return neu;
      });
    } catch (fehler) {
      const befund = klassifiziereFehler(fehler, { schreibzugriff: true });
      setRowErrors((vorher) => new Map(vorher).set(principalId, meldungFuerKontoaktion(befund)));
    } finally {
      setBusyIds((vorher) => {
        const neu = new Set(vorher);
        neu.delete(principalId);
        return neu;
      });
    }
  }

  async function handleLogout(): Promise<void> {
    try {
      await logout();
      setSession(null);
      setRechte(null);
      setError(null);
    } catch {
      setError('Abmelden fehlgeschlagen. Bitte versuchen Sie es erneut.');
    }
  }

  return (
    <main>
      <h1>ASAS Core</h1>
      <SessionPanel
        session={session}
        loading={loading}
        error={error}
        onLogout={() => {
          void handleLogout();
        }}
      />
      <TrpcPanel echo={echo} loading={trpcLoading} error={trpcError} />
      {rechte !== null && ansichtSichtbar('nutzerverwaltung', rechte) && (
        <AdminPanel
          geoeffnet={adminGeoeffnet}
          onOeffnen={() => {
            setAdminGeoeffnet(true);
          }}
          principals={principals}
          roles={roles}
          loading={principalsLoading}
          error={principalsError}
          busyIds={busyIds}
          rowErrors={rowErrors}
          onLock={(principalId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.lock.mutate({ principalId }),
            );
          }}
          onUnlock={(principalId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.unlock.mutate({ principalId }),
            );
          }}
          onAssignRole={(principalId, roleId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.assignRole.mutate({ principalId, roleId }),
            );
          }}
          onRevokeRole={(principalId, roleId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.revokeRole.mutate({ principalId, roleId }),
            );
          }}
        />
      )}
      {rechte !== null && ansichtSichtbar('arbeitspaketBibliothek', rechte) && (
        <ArbeitspaketBibliothek darfSchreiben={rechte.has('work_package_library:manage')} />
      )}
      <p>
        <small>
          Domain-Version <strong data-testid="domain-version">{DOMAIN_VERSION}</strong>
        </small>
      </p>
    </main>
  );
}
